import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const EmployeeTurnover = () => {
  // State variables for Employee Turnover calculation
  const [employeesLeaving, setEmployeesLeaving] = useState(0); // Number of employees leaving
  const [averageEmployees, setAverageEmployees] = useState(0); // Average number of employees during the period
  const [turnoverRate, setTurnoverRate] = useState(0); // Calculated turnover rate

  // Event handlers to update state values
  const handleEmployeesLeavingChange = (e) => setEmployeesLeaving(parseInt(e.target.value) || 0);
  const handleAverageEmployeesChange = (e) => setAverageEmployees(parseInt(e.target.value) || 0);

  // useEffect hook to calculate the turnover rate when any input changes
  useEffect(() => {
    if (employeesLeaving && averageEmployees) {
      const rate = (employeesLeaving / averageEmployees) * 100; // Calculate turnover rate
      setTurnoverRate(rate);
    } else {
      setTurnoverRate(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [employeesLeaving, averageEmployees]);

  return (
    <Layout title="Employee Turnover Calculator" path="employee-turnover">
      <section className="section-padding" id="employee-turnover-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Employee Turnover Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Employee Turnover Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Number of Employees Leaving Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="employeesLeaving" className="form-label">
                        Number of Employees Leaving
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Employees Leaving:</strong> Enter the total number of employees who left the organization during a specific period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="employeesLeaving"
                        placeholder="Enter the number of employees leaving"
                        value={employeesLeaving || ""}
                        onChange={handleEmployeesLeavingChange}
                      />
                    </div>

                    {/* Average Number of Employees Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="averageEmployees" className="form-label">
                        Average Number of Employees
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Average Employees:</strong> Enter the average number of employees in your organization during the same period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="averageEmployees"
                        placeholder="Enter the average number of employees"
                        value={averageEmployees || ""}
                        onChange={handleAverageEmployeesChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Employee Turnover Rate:{" "}
                        <span className="fw-bold ms-3">
                          {turnoverRate === 0 ? "0.00" : turnoverRate.toFixed(2)} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Employee Turnover Calculator
                </h3>

                {/* What is an Employee Turnover Calculator? */}
                <h4 className="mt-3 mb-3">What is an Employee Turnover Calculator?</h4>
                <p>
                  An **Employee Turnover Calculator** helps organizations calculate the turnover rate, which is the percentage of employees who leave a company during a given period. This metric is a vital indicator of employee retention, organizational health, and workforce stability. By understanding turnover, companies can take steps to improve employee satisfaction and reduce unnecessary hiring costs.
                </p>

                {/* Why Use an Employee Turnover Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Employee Turnover Calculator?</h4>
                <p>
                  This calculator is a useful tool for tracking the number of employees leaving relative to the total workforce size. A high turnover rate may indicate issues such as low employee engagement, poor company culture, or insufficient career development opportunities. This tool helps HR departments and management assess how well they are retaining employees and take steps to address retention challenges.
                </p>

                {/* Benefits of Using an Employee Turnover Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Employee Turnover Calculator</h4>
                <ul>
                  <li><strong>Helps HR departments understand retention challenges:</strong> Provides valuable insights into how many employees are leaving and why.</li>
                  <li><strong>Supports improvement in employee engagement:</strong> Identifying turnover problems can help HR take action to improve engagement and job satisfaction.</li>
                  <li><strong>Useful for workforce planning and development:</strong> Knowing the turnover rate aids in recruitment and workforce planning for the future.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Employee Turnover Calculator?</h4>
                <p>
                  The Employee Turnover Calculator is ideal for:
                </p>
                <ul>
                  <li><strong>HR Departments:</strong> To monitor and manage employee retention levels and understand turnover trends.</li>
                  <li><strong>Management:</strong> To evaluate the effectiveness of retention strategies and make data-driven decisions about employee engagement.</li>
                  <li><strong>Consulting Firms:</strong> To assess client organizations' workforce dynamics and suggest strategies for improving employee retention.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is considered a high employee turnover rate?</h4>
                <p>
                  A high turnover rate varies by industry, but a turnover rate above 10-15% per year can be considered high in many sectors. It often suggests that the organization needs to improve retention strategies or address issues related to employee satisfaction.
                </p>

                <h4 className="mt-4 mb-3">What can companies do to reduce employee turnover?</h4>
                <p>
                  Companies can reduce turnover by improving workplace culture, offering competitive benefits, recognizing employee achievements, providing career development opportunities, and fostering a healthy work-life balance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EmployeeTurnover;
